import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2, UserPlus } from 'lucide-react';
import { insertLead } from '@/lib/clientsData';
import { toast } from 'sonner';

// "How did you hear about us?" options — captured to `source` so you can see
// which channels actually produce leads. (Job type lives on the estimate.)
export const LEAD_SOURCE_OPTIONS = [
  'Google',
  'Facebook',
  'Instagram',
  'Referral (family/friend)',
  'Website',
  'Walk-in',
  'Repeat customer',
  'Other',
];

const initialState = {
  name: '',
  phone: '',
  email: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  source: '',
  notes: '',
};

export const AddLeadDialog = ({ open, onOpenChange, onCreated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setForm(initialState);
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const setField = (key) => (e) => {
    const v = e?.target?.value ?? '';
    setForm((f) => ({ ...f, [key]: v }));
  };

  const validate = () => {
    if (!form.name.trim()) return t('clientsLeads.addDialog.nameRequired');
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      return t('clientsLeads.addDialog.nameRequired'); // generic validation fallback; precise key in next polish pass
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const created = await insertLead({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address_street: form.address_street.trim(),
        address_city: form.address_city.trim(),
        address_state: form.address_state.trim(),
        address_zip: form.address_zip.trim(),
        source: form.source.trim(),
        notes: form.notes,
        type: 'lead',
      });
      toast.success(t('clientsLeads.addDialog.createdToast'));
      onOpenChange?.(false);
      onCreated?.(created);
    } catch (err) {
      setError(err?.message || t('clientsLeads.addDialog.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-lg"
        data-testid="add-lead-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <UserPlus size={16} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {t('clientsLeads.addDialog.eyebrow')}
            </span>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            {t('clientsLeads.addDialog.title')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('clientsLeads.addDialog.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-1" noValidate>
          <div className="space-y-2">
            <Label
              htmlFor="lead-name"
              className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              {t('clientsLeads.addDialog.nameLabel')}
            </Label>
            <Input
              id="lead-name"
              value={form.name}
              onChange={setField('name')}
              placeholder={t('clientsLeads.addDialog.namePlaceholder')}
              autoComplete="off"
              autoFocus
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 focus-visible:border-[hsl(var(--primary))]/40"
              data-testid="add-lead-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label
                htmlFor="lead-phone"
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
              >
                {t('clientsLeads.addDialog.phoneLabel')}
              </Label>
              <Input
                id="lead-phone"
                value={form.phone}
                onChange={setField('phone')}
                placeholder="555-555-5555"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="add-lead-phone"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="lead-email"
                className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
              >
                {t('clientsLeads.addDialog.emailLabel')}
              </Label>
              <Input
                id="lead-email"
                type="email"
                value={form.email}
                onChange={setField('email')}
                placeholder="name@example.com"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="add-lead-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label
              className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              {t('clientsLeads.addDialog.addressLabel')}
            </Label>
            <Input
              id="lead-address-street"
              value={form.address_street}
              onChange={setField('address_street')}
              placeholder="Street"
              autoComplete="off"
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
              data-testid="add-lead-address-street"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Input
                id="lead-address-city"
                value={form.address_city}
                onChange={setField('address_city')}
                placeholder="City"
                autoComplete="off"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60"
                data-testid="add-lead-address-city"
              />
              <Input
                id="lead-address-state"
                value={form.address_state}
                onChange={setField('address_state')}
                placeholder="State"
                autoComplete="off"
                maxLength={4}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60"
                data-testid="add-lead-address-state"
              />
              <Input
                id="lead-address-zip"
                value={form.address_zip}
                onChange={setField('address_zip')}
                placeholder="ZIP"
                autoComplete="off"
                maxLength={10}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60"
                data-testid="add-lead-address-zip"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="lead-source"
              className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              How did you hear about us?
            </Label>
            <Select
              value={form.source}
              onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}
            >
              <SelectTrigger
                id="lead-source"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                data-testid="add-lead-source"
              >
                <SelectValue placeholder="Select a source…" />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border">
                {LEAD_SOURCE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="lead-notes"
              className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              {t('clientsLeads.addDialog.notesLabel')}
            </Label>
            <Textarea
              id="lead-notes"
              rows={3}
              value={form.notes}
              onChange={setField('notes')}
              placeholder={t('clientsLeads.addDialog.notesPlaceholder')}
              className="bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 resize-none"
              data-testid="add-lead-notes"
            />
          </div>

          {error ? (
            <Alert
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="add-lead-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            >
              {t('clientsLeads.addDialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="add-lead-submit"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {t('clientsLeads.addDialog.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadDialog;
